import { DiseaseForPlan } from "fe-common/graphql/dao";
import { useMemo } from "react";
import { DiseaseCluster } from "fe-common/hooks/sortFeaturesWithCrs";
import {
  DiseasesAliasing,
  QualifiedFeatureFragment,
  SortType,
} from "fe-common/graphql";
import { DEFAULT_SORT } from "./consts";

const boostProbForIncludedDiseases = (
  diseases,
  includedInPlanWithSubTypes: DiseaseCluster[],
  diseasesForPlan: DiseaseForPlan[],
) => {
  const includedInPlanSubTypes = includedInPlanWithSubTypes
    .flatMap((includedInPlan) => includedInPlan?.crs)
    // Filter out nulls
    .filter((i) => i);

  //all diseases are included in plan, set them all to 1
  if (
    diseases
      .flatMap((d) => d.crs)
      .every((topCr) => includedInPlanSubTypes.some((cr) => topCr === cr))
  ) {
    diseasesForPlan.forEach((d) => (d.prob = 1));
  } else if (includedInPlanWithSubTypes.length) {
    //for each included disease, boost its strongest sub type to the maximums prob
    const maxProb = Math.max(...diseasesForPlan.map((cr) => cr.prob));

    includedInPlanWithSubTypes.forEach((included) => {
      const boostDisease = diseasesForPlan.find(
        (d) =>
          d.disease.feature.id === included?.crs[0]?.disease[0]?.feature?.id,
      );
      boostDisease.prob = maxProb;
    });
  }
};

export const useGetDiseasesSubTypesForPlan = ({
  diseases,
  diseasesToIncludeInPlan,
  sortType = DEFAULT_SORT,
}: {
  diseases: DiseaseCluster[];
  diseasesToIncludeInPlan: QualifiedFeatureFragment[];
  sortType: SortType;
}): { diseasesForPlan: DiseaseForPlan[]; aliasing: DiseasesAliasing[] } =>
  useMemo(() => {
    if (!diseases) return { diseasesForPlan: [], aliasing: [] };

    const includedInPlanWithSubTypes: DiseaseCluster[] =
      diseasesToIncludeInPlan
        ?.map((iip) =>
          diseases.find(
            (s) => s?.qf?.feature?.id && s.qf.feature.id === iip.feature.id,
          ),
        )
        .filter((i) => i) || [];
    const distinctCrsForPlan = [...new Set(diseases?.flatMap((e) => e?.crs))];
    const aliasing: DiseasesAliasing[] = diseases
      .flatMap((d) =>
        d.crs.map((cr) => ({
          d1: cr.disease[0].id,
          d2: d.crs[0].disease[0].id,
        })),
      )
      .filter((alias) => alias.d1 !== alias.d2);
    const diseasesForPlan = !diseases
      ? []
      : distinctCrsForPlan.map((cr) => ({
          disease: cr?.disease[0],
          prob: cr.scores?.find((s) => s.sortType === sortType).adjusted || 0, //TODO in useSortedFeaturesWithCrs: we should probebly create our own CR structure which already have the right sorttype selected
        }));

    boostProbForIncludedDiseases(
      diseases,
      includedInPlanWithSubTypes,
      diseasesForPlan,
    );

    return { diseasesForPlan, aliasing };
  }, [diseases, diseasesToIncludeInPlan, sortType]);
